/**
 * Bootstrap logic for the embedded search demo bundle (item #7970).
 *
 * The VO landing page (com-visitingobjects) swaps the Search showcase's static
 * screenshot for the live widget demo. The demo ships as its own bundle
 * (vo-search-demo.js, built from demo-standalone.jsx) so the customer-facing
 * vo-search.js never carries the fixture dataset or autoplay code.
 *
 * This module is the pure, testable part: locate the container, read its
 * config, inject the widget CSS, and hand off to an injected mount function.
 */

/**
 * Shared style element id. Matches standalone.jsx's injection id so a page
 * that somehow loads both bundles still injects the widget CSS exactly once.
 */
import { parseShapeAttr, parseOverlayScopeAttr } from './shape_attr';
import { parsePaceAttr, parseStartAttr } from './demo-autoplay';
import { parseAccentAttr, parseLookAttr, parsePresetAttr, parseResultPartsAttr, parseThemeAttr } from './demo-theme';

export const DEMO_STYLE_ELEMENT_ID = 'vo-search-styles';

/**
 * Find every demo embed container, in document order.
 *
 * Singular until item #8685: the page could render as many containers as it
 * liked and only the first ever became a widget. That is the limit #8665 runs
 * into -- four independently-shaped demos on one page is the acceptance test for
 * a placement carrying its own shape.
 *
 * @param {Document} doc - Document to search
 * @returns {Element[]} The [data-vo-search-demo] elements
 */
export function findDemoContainers(doc) {
  return Array.from(doc.querySelectorAll('[data-vo-search-demo]'));
}

/**
 * Find the first demo embed container.
 *
 * @param {Document} doc - Document to search
 * @returns {Element|null} The [data-vo-search-demo] element, or null
 */
export function findDemoContainer(doc) {
  return doc.querySelector('[data-vo-search-demo]');
}

/**
 * Read the embed config from a demo container.
 *
 * The shape and overlay scope are per CONTAINER (item #8686), which is what
 * lets one instance render four differently-shaped demos on one page. Both are
 * the sanctioned first-party pin from decision #5050 -- /v5 is VO's own chrome.
 * Neither writes anything: instance 3339's server shape is untouched (decision
 * #4951), these only override what this one container renders.
 *
 * @param {Element|null} container - The demo container element
 * The theme is the same kind of pin (item i21806): which theme this container
 * shows, known themes only.
 *
 * So is the preset (item i22491): a named DEMO_PRESETS entry, known names only.
 * The film's autoplay pins (item i22492) name a pace and a starting query.
 * The result-parts pin (item i22906) names which parts of a result show.
 *
 * @returns {{clientPublicToken: string, shape: Object|null, overlayScope: string, theme: string|null, look: string|null, accent: string|null, preset: string|null, autoplayPace: string|null, autoplayStart: number|null, resultParts: string[]|null}|null}
 *   Config, or null when the container is missing or tokenless (a tokenless
 *   widget is inert)
 */
export function readDemoEmbedConfig(container) {
  if (!container) return null;
  const identity = readDemoIdentity(container);
  if (!identity) return null;
  return {
    ...identity,
    shape: parseShapeAttr(container.getAttribute('data-vo-search-shape')),
    overlayScope: parseOverlayScopeAttr(container.getAttribute('data-vo-search-overlay-scope')),
    autoOpen: parseAutoOpenAttr(container.getAttribute('data-vo-search-auto-open')),
    theme: parseThemeAttr(container.getAttribute('data-vo-search-theme')),
    look: parseLookAttr(container.getAttribute('data-vo-search-look')),
    accent: parseAccentAttr(container.getAttribute('data-vo-search-accent')),
    preset: parsePresetAttr(container.getAttribute('data-vo-search-preset')),
    autoplayPace: parsePaceAttr(container.getAttribute('data-vo-search-autoplay-pace')),
    autoplayStart: parseStartAttr(container.getAttribute('data-vo-search-autoplay-start')),
    resultParts: parseResultPartsAttr(container.getAttribute('data-vo-search-result-parts')),
  };
}

/**
 * Which instance a container demonstrates, in one of two forms (item #17619,
 * decision #6093): a credential the page already holds (data-public-token),
 * or the instance's id (data-demo-index) for the bundle to resolve into this
 * environment's CURRENT credential at bootstrap. A token baked into a static
 * site at build time is one environment's mint and 400s on every other; the
 * id is what a static site may ship.
 *
 * A token present wins outright and the conflict is named: the site should
 * ship one or the other, and a page carrying both has a template bug worth
 * seeing in the console rather than a silent preference.
 *
 * @param {Element} container
 * @returns {{clientPublicToken: string|null, demoIndex: string|null}|null}
 *   null when the container names no instance at all (inert, not broken)
 */
function readDemoIdentity(container) {
  const token = container.getAttribute('data-public-token');
  const demoIndex = container.getAttribute('data-demo-index');
  if (token && demoIndex) {
    console.warn('[vo-search-demo] container carries both data-public-token and data-demo-index; using the token');
  }
  if (token) return { clientPublicToken: token, demoIndex: null };
  if (demoIndex) return { clientPublicToken: null, demoIndex };
  return null;
}

/**
 * Read the auto-open attribute (item #8844). Present means yes, as a boolean
 * HTML attribute does, with an explicit "false" honoured so a template can
 * compute the value without omitting the attribute.
 *
 * @param {string|null} value - the attribute value
 * @returns {boolean}
 */
function parseAutoOpenAttr(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim().toLowerCase() !== 'false';
}

/**
 * Inject the widget CSS once.
 *
 * @param {Document} doc - Document to inject into
 * @param {string} cssText - The bundled vo-search.css text
 */
export function injectDemoStyles(doc, cssText) {
  if (doc.getElementById(DEMO_STYLE_ELEMENT_ID)) return;
  const style = doc.createElement('style');
  style.id = DEMO_STYLE_ELEMENT_ID;
  style.textContent = cssText;
  doc.head.appendChild(style);
}

/**
 * How to stop the demo living in each container, for good (item #9013).
 *
 * Registered by whoever mounts a demo, because the embed knows containers and
 * the widget knows autoplay, and the swap needs to get from one to the other.
 */
const demoHalts = new WeakMap();

/**
 * Record how to halt the demo mounted in a container (item #9013).
 *
 * @param {Element} container - The [data-vo-search-demo] element
 * @param {function(): void} halt - Stops that demo's autoplay permanently
 */
export function registerDemoHalt(container, halt) {
  if (container) demoHalts.set(container, halt);
}

/**
 * Forget how to halt a container's demo, when it no longer has one.
 *
 * @param {Element|null} container - The [data-vo-search-demo] element
 */
export function unregisterDemoHalt(container) {
  if (container) demoHalts.delete(container);
}

/**
 * Swap a demo to its screenshot fallback (plan #4906): hide the live widget
 * wrapper ([data-vo-search-demo-live]), reveal the hidden fallback
 * ([data-vo-search-demo-fallback]) the Astro embed rendered alongside it, and
 * halt THAT demo's autoplay so it stops firing into a hidden tree. A demo
 * without the fallback markup is left untouched.
 *
 * The halt reaches one demo (item #9013). It used to be dispatched on `window`,
 * where the only listener answered with the permanent user-takeover path -- so
 * one panel's failed health check killed autoplay in whichever demo the visitor
 * was actually looking at. That was right while a page carried one demo, when
 * "the demo that failed" and "the only demo on the page" were the same thing;
 * the tabbed surface (#8852/#8854) made them different.
 *
 * The pair is resolved relative to the CONTAINER when one is given (item
 * #8685). Document-scoped, one demo failing took down the first pair on the
 * page -- some other demo's -- and left the broken one live. Omitting the
 * container keeps the old document-wide behaviour for the single-demo pages
 * that predate this.
 *
 * @param {Document} doc - Document to swap in
 * @param {Element} [container] - The failing [data-vo-search-demo] element
 * @returns {boolean} True when the fallback was revealed
 */
export function swapDemoToFallback(doc, container) {
  const live = container
    ? container.closest('[data-vo-search-demo-live]')
    : doc.querySelector('[data-vo-search-demo-live]');
  // The fallback is the live wrapper's sibling, so scope through the shared
  // parent rather than searching the document and hoping the first hit is this
  // demo's.
  const scope = live && live.parentElement ? live.parentElement : doc;
  const fallback = scope.querySelector('[data-vo-search-demo-fallback]');
  if (!fallback) return false;
  if (live) {
    live.hidden = true;
    // `hidden` alone is not enough (item #8720): the UA `[hidden] { display: none }`
    // rule loses to any author-origin display on the wrapper -- both consumers
    // set one (`.mode-showcase-live` and `.search-demo-live` are `display: flex`),
    // so the failed widget stayed on screen above the revealed screenshot. The
    // inline display outranks every consumer stylesheet short of !important.
    live.style.display = 'none';
  }
  fallback.hidden = false;
  const halt = container && demoHalts.get(container);
  if (halt) halt();
  return true;
}

/**
 * Reverse a fallback swap: show the demo again and hide its screenshot (item
 * #9014).
 *
 * The swap had no reverse anywhere, so a panel that had ever shown its
 * photograph showed it for the rest of the page's life -- and mounting a demo
 * into that panel put a working widget BEHIND a still nothing ever took down.
 * Mounting is the moment the photograph stops being true, so mounting is what
 * reverses it.
 *
 * The inline `display` the swap sets has to be cleared, not just the `hidden`
 * attribute: it exists precisely because the UA `[hidden]` rule loses to the
 * consumers' author-origin display (item #8720), so clearing the attribute
 * alone would leave the wrapper displayed:none behind an unhidden fallback.
 * Decision #5078's criterion is that a panel never shows both, in either
 * direction.
 *
 * @param {Document} doc - Document to reveal in
 * @param {Element} [container] - The [data-vo-search-demo] element being mounted
 * @returns {boolean} True when the pair was found and put back
 */
export function revealDemoLive(doc, container) {
  const live = container
    ? container.closest('[data-vo-search-demo-live]')
    : doc.querySelector('[data-vo-search-demo-live]');
  const scope = live && live.parentElement ? live.parentElement : doc;
  const fallback = scope.querySelector('[data-vo-search-demo-fallback]');
  if (!live || !fallback) return false;
  live.hidden = false;
  live.style.removeProperty('display');
  fallback.hidden = true;
  return true;
}

/** What an in-place surface renders once it is alive. */
const DEMO_INPUT_SELECTOR = '.vo-search-search-input';

/** What an overlay surface renders before the visitor opens it. */
const DEMO_TRIGGER_SELECTOR = '.vo-search-search-modal-trigger, .vo-search-bar-trigger';

/**
 * True when the mounted widget looks alive FOR THE SHAPE IT RESOLVED, and no
 * error state is showing. Covers the failure modes the fallback exists for --
 * a config fetch that never resolves renders nothing at all, and a failed first
 * query renders the error message.
 *
 * The shape matters (item #8721). An overlay-surfaced widget renders only its
 * trigger; its input lives in the overlay, which is closed, and pre-opening it
 * is not available (decision #4963). Demanding an input declared every overlay
 * demo on the display-modes band dead at the deadline even though all four had
 * mounted correctly.
 *
 * A demo with no pin has no shape to test against -- the shape is server config
 * the embed never sees -- so either marker counts. Rendering one of them is
 * proof the widget mounted, which is what the fallback is judging.
 *
 * The widget mounts inside the container's shadow root (item #8774), and
 * querySelector stops at that boundary -- so the markers are looked for in the
 * shadow root when there is one. Reading only the light DOM would declare every
 * healthy demo dead at the deadline and swap four working demos to four
 * screenshots, which is the #8721 failure from the other direction.
 *
 * @param {Element} container - The [data-vo-search-demo] element
 * @param {{trigger: string, surface: string, layout: string}} [shape] - The
 *   container's pinned shape, when it has one
 * @returns {boolean}
 */
export function isDemoHealthy(container, shape) {
  if (!container) return false;
  const mounted = container.shadowRoot || container;
  if (mounted.querySelector('.vo-search-error-message')) return false;

  const hasInput = !!mounted.querySelector(DEMO_INPUT_SELECTOR);
  const hasTrigger = !!mounted.querySelector(DEMO_TRIGGER_SELECTOR);
  if (!shape) return hasInput || hasTrigger;

  // Trigger 'none' has no control to look for and forces the in-place surface,
  // exactly as resolveRenderShape repairs the pair: with no trigger, an overlay
  // would leave the visitor no way to reach the search UI at all.
  const overlaySurfaced = shape.surface === 'overlay' && shape.trigger !== 'none';
  return overlaySurfaced ? hasTrigger : hasInput;
}

/**
 * Open a demo's overlay by clicking the widget's OWN trigger (item #8844).
 *
 * An overlay-surfaced widget mounts closed, so a demo of the popup shows a lone
 * icon unless something opens it. The something is a real click on the real
 * control: measured on the live page, clicking `.vo-search-search-modal-trigger`
 * inside the container's shadow root moved `hasInput` and `overlayOpen` from
 * false to true. The widget still mounts closed, still resolves its configured
 * shape, and opens through the handler a visitor's click reaches.
 *
 * What this is NOT: `defaultOpen` / `isContainedModal` were deleted by decision
 * #4963, kept dead by #5067, and are guarded -- the widget rejects both. Nor can
 * this be the global `vo-search-open` event: #5074 excludes scoped overlays from
 * it on purpose, because the event carries no placement identity, so one sender
 * would open every scoped panel on the page. The trigger is the only sender with
 * a claim on one overlay.
 *
 * The widget renders after its config fetch resolves, so a first look that finds
 * no trigger is normal; this retries until the deadline and then stops asking.
 *
 * Demo bundle only. Nothing in this module reaches vo-search.js.
 *
 * @param {Element} container - The [data-vo-search-demo] element
 * @param {Object} [opts]
 * @param {{trigger: string, surface: string, layout: string}} [opts.shape] - The
 *   container's pinned shape. An in-place shape has nothing to open, so it is
 *   left alone; an unpinned container is judged on what it rendered.
 * @param {number} [opts.timeoutMs=8000] - How long to wait for the widget to mount
 * @param {number} [opts.intervalMs=100] - Gap between looks
 * @param {function(Function, number): *} [opts.schedule] - Timer (default setTimeout)
 * @returns {boolean} True when the trigger was clicked on this look
 */
export function autoOpenDemo(container, { shape, timeoutMs = 8000, intervalMs = 100, schedule = (fn, ms) => setTimeout(fn, ms) } = {}) {
  if (!container) return false;
  // An in-place surface renders its input in the page: there is no overlay, and
  // clicking around looking for one would be looking for something the shape
  // says does not exist. Same repair resolveRenderShape applies -- trigger
  // 'none' is in-place whatever surface the pin claims.
  if (shape && !(shape.surface === 'overlay' && shape.trigger !== 'none')) return false;

  let clicked = false;
  let waited = 0;

  function look() {
    if (clicked) return true;
    const mounted = container.shadowRoot || container;
    const trigger = mounted.querySelector(DEMO_TRIGGER_SELECTOR);
    if (trigger) {
      clicked = true;
      trigger.click();
      return true;
    }
    if (waited >= timeoutMs) return false;
    waited += intervalMs;
    schedule(look, intervalMs);
    return false;
  }

  return look();
}

/**
 * The cancel function for each container's live health watch (item #9012).
 *
 * Keyed by container because the code that takes a demo down is not always the
 * code that started its watch: the bootstrap starts the watch on the tab a page
 * loads selected, and the page's tab script is what unmounts it. A canceller
 * handed only to the caller would leave that demo -- the one every visitor meets
 * first -- holding the timer this defect is made of.
 */
const healthWatches = new WeakMap();

/**
 * Schedule a one-shot health check on the mounted demo; an unhealthy widget
 * at the deadline swaps the embed to the screenshot fallback.
 *
 * The returned canceller must be called when the demo is taken down on purpose
 * (item #9012). Until it was cancellable, leaving a tab within the deadline --
 * every realistic cycle of the landing's band -- fired the check against a
 * shadow root that had just been emptied, so a healthy demo the visitor merely
 * navigated away from was declared dead and its panel swapped to a photograph.
 *
 * Cancelling raises a flag the deadline reads rather than clearing a timer,
 * because the timer belongs to an injected scheduler this module cannot assume
 * anything about. The pending callback still runs; it just has nothing to say.
 *
 * @param {Document} doc - Document the demo lives in
 * @param {Object} opts
 * @param {Element} opts.container - The [data-vo-search-demo] element
 * @param {{trigger: string, surface: string, layout: string}} [opts.shape] - The
 *   container's pinned shape, which decides what "alive" looks like (item #8721)
 * @param {number} [opts.timeoutMs=8000] - Grace period for mount + config fetch + first query
 * @param {function(Function, number): *} [opts.schedule] - Timer (default setTimeout)
 * @returns {function(): void} Cancels this watch
 */
export function watchDemoHealth(doc, { container, shape, timeoutMs = 8000, schedule = (fn, ms) => setTimeout(fn, ms) }) {
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  schedule(() => {
    if (cancelled) return;
    if (!isDemoHealthy(container, shape)) {
      swapDemoToFallback(doc, container);
    }
  }, timeoutMs);
  if (container) healthWatches.set(container, cancel);
  return cancel;
}

/**
 * Cancel the health watch a container is currently under, if any (item #9012).
 *
 * Only the watch in flight: a container mounted again is watched again, so a
 * tab that fails on the visitor's second visit still shows its still.
 *
 * @param {Element|null} container - The [data-vo-search-demo] element
 * @returns {boolean} True when there was a watch to cancel
 */
export function cancelDemoHealthWatch(container) {
  const cancel = container && healthWatches.get(container);
  if (!cancel) return false;
  cancel();
  healthWatches.delete(container);
  return true;
}

/**
 * Initialize every demo embed on the page: locate the containers, resolve each
 * one's config, inject styles once, and bring each up. Degrades to zero
 * footprint (no styles, no mount) when the page carries no configured demo
 * container.
 *
 * Each demo stands or falls on its own (item #8685): a mount failure swaps THAT
 * demo to its screenshot immediately and the rest still mount, and each
 * successful mount is health-checked separately so one demo's config-fetch
 * failure does not blank the others.
 *
 * A container naming its instance by id (data-demo-index) is brought up once
 * its token resolves (item #17619); the return value counts it as configured,
 * because the page did configure it -- what it is waiting on is the
 * environment, not the markup.
 *
 * @param {Document} doc - Document to bind to
 * @param {Object} deps - Injected dependencies
 * @param {function(Element, {clientPublicToken: string}): void} deps.mount -
 *   Mounts the demo React tree into a container
 * @param {string} deps.cssText - The bundled widget CSS
 * @param {function(string): Promise<string>} [deps.resolveToken] - Resolves a
 *   demo index to its current public token; without it an index-only
 *   container is inert
 * @param {function(Function, number): *} [deps.schedule] - Health-check timer
 * @returns {boolean} True when at least one demo was mounted or is resolving
 */
export function initDemoEmbed(doc, { mount, cssText, resolveToken, schedule }) {
  const configured = findDemoContainers(doc)
    .map((container) => ({ container, config: readDemoEmbedConfig(container) }))
    // A container naming no instance is inert rather than broken: the page
    // rendered a demo slot it never configured, which is not a failure to fall
    // back from.
    .filter((entry) => entry.config !== null)
    .filter((entry) => entry.config.clientPublicToken || canResolve(resolveToken, entry.container));
  if (configured.length === 0) return false;

  injectDemoStyles(doc, cssText);

  let up = 0;
  configured.forEach(({ container, config }) => {
    if (bringDemoUp(doc, { container, config, mount, resolveToken, schedule })) up += 1;
  });

  return up > 0;
}

/**
 * Whether an index-only container has any way to become a widget. Without a
 * resolver it cannot, and that is a wiring fault worth a console line rather
 * than a fallback swap: the page did nothing wrong.
 */
function canResolve(resolveToken, container) {
  if (typeof resolveToken === 'function') return true;
  console.error('[vo-search-demo] container names data-demo-index but no token resolver was provided:', container);
  return false;
}

/**
 * Containers whose token is being resolved, each mapped to the handle that
 * cancels its pending mount (item #17619).
 */
const pendingMounts = new WeakMap();

/**
 * Bring one demo container up: mount it, open it if asked, and watch its
 * health. The one place both the bootstrap and an on-request mount go through,
 * so a demo behaves the same whichever path woke it.
 *
 * A token-bearing config mounts synchronously. An index-bearing config first
 * resolves its token; while that is in flight a second request for the same
 * container is refused rather than started twice, and a resolution that fails
 * -- any non-200, any transport error -- swaps the container to its fallback
 * at once, with no retry: the still is the designed answer to a demo that
 * cannot bootstrap (commit 706ec59), and an inert widget would be worse.
 *
 * @param {Document} doc
 * @param {Object} opts
 * @param {Element} opts.container
 * @param {{clientPublicToken: string|null, demoIndex: string|null, shape: Object|null, autoOpen: boolean}} opts.config
 * @param {function(Element, Object): void} opts.mount
 * @param {function(string): Promise<string>} [opts.resolveToken]
 * @param {function(Function, number): *} [opts.schedule]
 * @returns {boolean} True when this call mounted the demo or started resolving it
 */
export function bringDemoUp(doc, { container, config, mount, resolveToken, schedule }) {
  if (config.clientPublicToken) {
    return mountAndWatch(doc, { container, config, mount, schedule });
  }
  if (pendingMounts.has(container) || typeof resolveToken !== 'function') return false;

  const handle = { cancelled: false };
  pendingMounts.set(container, handle);
  resolveToken(config.demoIndex)
    .then((clientPublicToken) => {
      if (handle.cancelled) return;
      pendingMounts.delete(container);
      mountAndWatch(doc, { container, config: { ...config, clientPublicToken }, mount, schedule });
    })
    .catch((err) => {
      if (handle.cancelled) return;
      pendingMounts.delete(container);
      console.error('[vo-search-demo] demo index', config.demoIndex, 'did not resolve to a token, swapping to screenshot fallback:', err);
      swapDemoToFallback(doc, container);
    });
  return true;
}

/**
 * Drop a token resolution in flight for a container, so a demo the page
 * un-requested before its token arrived is not mounted behind its back.
 *
 * @param {Element} container
 * @returns {boolean} True when a resolution was pending
 */
export function cancelPendingDemoMount(container) {
  const handle = container && pendingMounts.get(container);
  if (!handle) return false;
  handle.cancelled = true;
  pendingMounts.delete(container);
  return true;
}

function mountAndWatch(doc, { container, config, mount, schedule }) {
  try {
    mount(container, config);
  } catch (err) {
    console.error('[vo-search-demo] widget mount failed, swapping to screenshot fallback:', err);
    swapDemoToFallback(doc, container);
    return false;
  }
  // A container that asked to start open opens itself, through its own
  // trigger, once the widget has mounted (item #8844).
  if (config.autoOpen) {
    const open = { shape: config.shape };
    autoOpenDemo(container, schedule ? { ...open, schedule } : open);
  }
  // The container's own shape decides what a healthy mount looks like, so
  // each demo is judged as the shape it was pinned to (item #8721).
  const watch = { container, shape: config.shape };
  watchDemoHealth(doc, schedule ? { ...watch, schedule } : watch);
  return true;
}

/**
 * Build the resolver that turns a demo index into the current public token of
 * the environment at baseUrl, through search-ingest's demo-instance read
 * (item #17618). Any answer but a 200 carrying a token rejects, and the
 * caller's fallback handles it; no retry, because the endpoint's 404 is a
 * statement (not a demo instance here), not a transient.
 *
 * @param {{fetch: typeof fetch, baseUrl: string}} deps - baseUrl '' means
 *   same-origin, as the widget's own base URL rule has it
 * @returns {function(string): Promise<string>}
 */
export function createDemoTokenResolver({ fetch, baseUrl }) {
  return async (demoIndex) => {
    if (!/^\d+$/.test(String(demoIndex))) {
      throw new Error(`[vo-search-demo] demo index is not an instance id: ${demoIndex}`);
    }
    const response = await fetch(`${baseUrl}/api/search/demo-instance/${demoIndex}/token`, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`demo-instance token read answered ${response.status}`);
    }
    const body = await response.json();
    if (!body || typeof body.clientPublicToken !== 'string' || !body.clientPublicToken) {
      throw new Error('demo-instance token read carried no clientPublicToken');
    }
    return body.clientPublicToken;
  };
}
